export const VIDEO_REFERENCE_MODES = ['single', 'multi'] as const;
export type VideoReferenceMode = (typeof VIDEO_REFERENCE_MODES)[number];

export const VIDEO_REFERENCE_TEMPLATES = [
  'two-people',
  'product-scene',
  'person-product',
  'custom',
] as const;
export type VideoReferenceTemplate = (typeof VIDEO_REFERENCE_TEMPLATES)[number];

export const VIDEO_REFERENCE_ROLES = [
  'source',
  'person-a',
  'person-b',
  'product',
  'scene',
  'style',
] as const;
export type VideoReferenceRole = (typeof VIDEO_REFERENCE_ROLES)[number];

export const MAX_VIDEO_REFERENCE_ROLES = 9;

export const MULTI_REFERENCE_ROLE_OPTIONS = VIDEO_REFERENCE_ROLES.filter(
  (role): role is Exclude<VideoReferenceRole, 'source'> => role !== 'source'
);

const VIDEO_REFERENCE_ROLE_LABELS: Record<VideoReferenceRole, string> = {
  source: 'Source Image',
  'person-a': 'Person A',
  'person-b': 'Person B',
  product: 'Product',
  scene: 'Scene',
  style: 'Style Reference',
};

const VIDEO_REFERENCE_TEMPLATE_ROLES: Record<
  Exclude<VideoReferenceTemplate, 'custom'>,
  VideoReferenceRole[]
> = {
  'two-people': ['person-a', 'person-b'],
  'product-scene': ['product', 'scene'],
  'person-product': ['person-a', 'product'],
};

const DEFAULT_CUSTOM_ROLES: VideoReferenceRole[] = ['person-a', 'scene'];

export function getVideoReferenceRoleLabel(role: VideoReferenceRole) {
  return VIDEO_REFERENCE_ROLE_LABELS[role];
}

export function getVideoReferenceSlotLabel(
  roles: readonly VideoReferenceRole[],
  index: number
) {
  const role = roles[index];
  if (!role) return '';

  const label = getVideoReferenceRoleLabel(role);
  const roleCount = roles.filter((candidate) => candidate === role).length;
  if (roleCount < 2) return label;

  const occurrence = roles
    .slice(0, index + 1)
    .filter((candidate) => candidate === role).length;
  return `${label} ${occurrence}`;
}

export function getVideoReferenceRoles({
  customRoles,
  mode,
  template,
}: {
  customRoles?: VideoReferenceRole[];
  mode: VideoReferenceMode;
  template: VideoReferenceTemplate;
}) {
  if (mode === 'single') return ['source'] satisfies VideoReferenceRole[];
  if (template !== 'custom') return VIDEO_REFERENCE_TEMPLATE_ROLES[template];

  const normalized = normalizeVideoReferenceRoles(customRoles).filter(
    (role) => role !== 'source'
  );
  return normalized.length >= 2 ? normalized : DEFAULT_CUSTOM_ROLES;
}

export function normalizeVideoReferenceRoles(value: unknown) {
  const candidates = Array.isArray(value)
    ? value
    : typeof value === 'string'
      ? value.split(',')
      : [];
  const allowed = new Set<string>(VIDEO_REFERENCE_ROLES);
  return candidates
    .filter(
      (role): role is VideoReferenceRole =>
        typeof role === 'string' && allowed.has(role)
    )
    .slice(0, MAX_VIDEO_REFERENCE_ROLES);
}

export function serializeVideoReferenceRoles(roles: VideoReferenceRole[]) {
  return roles.join(',');
}

export function compileRoleAwareVideoPrompt({
  prompt,
  roles,
}: {
  prompt: string;
  roles: VideoReferenceRole[];
}) {
  const trimmedPrompt = prompt.trim();
  if (roles.length < 2) return trimmedPrompt;

  const mapping = roles
    .map(
      (_, index) =>
        `- Image ${index + 1} = ${getVideoReferenceSlotLabel(roles, index)}`
    )
    .join('\n');

  return [
    'Reference mapping:',
    mapping,
    '',
    `Motion request: ${trimmedPrompt}`,
    '',
    'Keep each referenced person and product visually consistent. Use scene and style references only for their named roles.',
  ].join('\n');
}

export function compileRoleAwareImageEditPrompt({
  prompt,
  roles,
  selectedIndex,
}: {
  prompt: string;
  roles: VideoReferenceRole[];
  selectedIndex: number;
}) {
  const selectedLabel = getVideoReferenceSlotLabel(roles, selectedIndex);
  if (!selectedLabel || roles.length < 2) return prompt.trim();

  const mapping = roles
    .map(
      (_, index) =>
        `- Image ${index + 1} = ${getVideoReferenceSlotLabel(roles, index)}`
    )
    .join('\n');

  return [
    'Reference mapping:',
    mapping,
    '',
    `Edit only Image ${selectedIndex + 1} (${selectedLabel}). Preserve the identity and appearance of the other references.`,
    `Edit request: ${prompt.trim()}`,
  ].join('\n');
}
